import { Link, createFileRoute } from "@tanstack/react-router";
import { CalendarClock, Compass, Orbit, Sparkles, Star, UsersRound } from "lucide-react";
import { SiteHeader } from "@/components/site-header";

export const Route = createFileRoute("/tu-vi-chi-tiet")({
  head: () => ({
    meta: [
      { title: "Giải Thích Tử Vi Đẩu Số - 12 Cung Mệnh & Chính Tinh, Phụ Tinh" },
      {
        name: "description",
        content:
          "Tìm hiểu Tử Vi Đẩu Số, 12 cung, chính tinh, phụ tinh, Mệnh, Thân, Cục và cách đọc lá số. Tìm hiểu ngay!",
      },
      { property: "og:title", content: "Giải Thích Tử Vi Đẩu Số - 12 Cung Mệnh & Chính Tinh" },
      {
        property: "og:description",
        content:
          "Nhập môn Tử Vi Đẩu Số: 12 cung, hệ thống chính tinh và phụ tinh, Mệnh — Thân — Cục.",
      },
      { property: "og:url", content: "https://boide.online/tu-vi-chi-tiet" },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/tu-vi-chi-tiet" }],
  }),
  component: TuViDetailPage,
});

const foundations = [
  {
    title: "Mệnh - Thân - Cục",
    icon: Compass,
    body: "Cung Mệnh mô tả cốt cách và nền khí. Cung Thân cho biết trọng tâm hành động khi trưởng thành. Cục nói về môi trường vận hành của lá số.",
  },
  {
    title: "12 Cung",
    icon: UsersRound,
    body: "Mỗi cung đại diện một lĩnh vực đời sống: bản thân, gia đình, sự nghiệp, tài lộc, hôn nhân, sức khỏe, quan hệ và môi trường sống.",
  },
  {
    title: "Hệ thống sao",
    icon: Star,
    body: "Chính tinh tạo trục tính chất chính; phụ tinh, cát tinh, sát tinh thêm sắc thái, trợ lực hoặc thử thách cho từng cung.",
  },
  {
    title: "Vận hạn",
    icon: CalendarClock,
    body: "Đại vận, tiểu vận và lưu niên cho thấy thời điểm một tiềm năng trong lá số được kích hoạt mạnh hay yếu.",
  },
];

const palaces = [
  "Mệnh",
  "Phụ Mẫu",
  "Phúc Đức",
  "Điền Trạch",
  "Quan Lộc",
  "Nô Bộc",
  "Thiên Di",
  "Tật Ách",
  "Tài Bạch",
  "Tử Tức",
  "Phu Thê",
  "Huynh Đệ",
];

const readingLayers = [
  "Bắt đầu từ Mệnh, Thân và Cục để nắm nền khí tổng thể.",
  "Xem tam phương tứ chính để hiểu các cung liên hệ trực tiếp với chủ đề đang hỏi.",
  "Đọc chính tinh trước, sau đó mới xét phụ tinh, cát tinh, sát tinh và trạng thái miếu/vượng/đắc/hãm.",
  "Đối chiếu đại vận, tiểu vận, lưu niên để biết thời điểm nào dễ ứng nghiệm.",
  "Tổng hợp nhiều dấu hiệu cùng chiều; không kết luận chỉ dựa vào một sao đơn lẻ.",
];

function TuViDetailPage() {
  return (
    <>
      <main className="flex-1 overflow-y-auto px-6 py-8 pb-24 lg:px-12 lg:pb-12">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-3xl border border-gold-soft bg-card/50 p-8 shadow-mystic backdrop-blur">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-gold-soft/60 bg-background/40 px-3 py-1 text-xs uppercase tracking-[0.25em] text-primary">
                <Orbit className="h-3.5 w-3.5" /> Nhập môn
              </span>
              <h1 className="mt-5 text-3xl font-semibold text-gold-gradient lg:text-5xl">
                Tử Vi là cách đọc cấu trúc vận mệnh theo lá số.
              </h1>
              <p className="mt-5 text-base leading-8 text-foreground/85 lg:text-lg">
                Tử Vi Đẩu Số dùng ngày giờ sinh để lập bản đồ 12 cung và hệ thống sao. Một lá số
                không nói một kết quả duy nhất, mà cho thấy khuynh hướng, điểm mạnh, thử thách và
                chu kỳ vận động của từng giai đoạn.
              </p>
            </div>
          </section>

          <section className="grid gap-4 md:grid-cols-2">
            {foundations.map((item) => (
              <article
                key={item.title}
                className="rounded-2xl border border-gold-soft/60 bg-background/30 p-5 backdrop-blur"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <item.icon className="h-4 w-4" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">{item.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">{item.body}</p>
              </article>
            ))}
          </section>

          <section className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
            <article className="rounded-2xl border border-gold-soft bg-card/40 p-6 backdrop-blur">
              <span className="text-xs uppercase tracking-[0.25em] text-primary">12 cung</span>
              <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {palaces.map((palace) => (
                  <div
                    key={palace}
                    className="rounded-xl border border-primary/15 bg-background/25 px-3 py-2 text-sm text-foreground/90"
                  >
                    {palace}
                  </div>
                ))}
              </div>
              <p className="mt-5 text-sm leading-7 text-muted-foreground">
                Khi luận một chủ đề, không chỉ xem cung trực tiếp mà còn xem các cung chiếu, tam hợp
                và vận hạn đang kích hoạt.
              </p>
            </article>

            <article className="rounded-2xl border border-gold-soft bg-card/40 p-6 backdrop-blur">
              <span className="text-xs uppercase tracking-[0.25em] text-primary">
                Cách đọc theo tầng
              </span>
              <ol className="mt-5 space-y-4">
                {readingLayers.map((step, index) => (
                  <li
                    key={step}
                    className="flex gap-4 rounded-xl border border-primary/15 bg-background/25 p-4"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold-gradient text-sm font-semibold text-primary-foreground">
                      {index + 1}
                    </span>
                    <p className="text-sm leading-7 text-foreground/85">{step}</p>
                  </li>
                ))}
              </ol>
            </article>
          </section>

          <section className="rounded-2xl border border-gold-soft bg-card/40 p-6 backdrop-blur">
            <span className="text-xs uppercase tracking-[0.25em] text-primary">
              Ví dụ đọc lá số
            </span>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <ExampleCard
                title="Cung Mệnh"
                body="Nếu Mệnh có chính tinh sáng và nhiều cát tinh, người đó thường có nền tính cách rõ, dễ gặp trợ lực khi đi đúng môi trường."
              />
              <ExampleCard
                title="Quan Lộc"
                body="Khi hỏi sự nghiệp, cần xem Quan Lộc cùng Mệnh, Tài Bạch và Thiên Di để biết năng lực, tiền bạc và môi trường bên ngoài."
              />
              <ExampleCard
                title="Đại vận"
                body="Một cung tốt nhưng chưa vào vận thì chưa chắc ứng mạnh; khi vận kích hoạt, cơ hội hoặc thử thách mới rõ rệt hơn."
              />
            </div>
          </section>

          <section className="rounded-2xl border border-gold-soft bg-background/30 p-6 text-center backdrop-blur">
            <Sparkles className="mx-auto h-8 w-8 text-primary" />
            <h3 className="mt-3 text-2xl font-semibold text-gold-gradient">
              Muốn xem lá số của bạn?
            </h3>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-muted-foreground">
              Tử Vi cần ngày giờ sinh càng chính xác càng tốt. Hãy xem lá số như một bản đồ tham
              khảo để hiểu mình và chọn hướng đi tỉnh táo hơn.
            </p>
            <Link
              to="/tu-vi"
              className="mt-5 inline-flex rounded-full bg-gold-gradient px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-gold transition hover:scale-[1.02]"
            >
              Lập lá số Tử Vi
            </Link>
          </section>
        </div>
      </main>
    </>
  );
}

function ExampleCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-primary/15 bg-background/25 p-4">
      <h4 className="font-semibold text-primary">{title}</h4>
      <p className="mt-2 text-sm leading-7 text-muted-foreground">{body}</p>
    </div>
  );
}
